import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Loader2, RefreshCw, Mail, MessageSquare, Bell, Check,
  CheckCheck, Users as UsersIcon, MapPin, Download
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_ICON = {
  in_app: Bell,
  email: Mail,
  sms: MessageSquare,
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'sent', label: 'Sent' },
  { key: 'read', label: 'Opened / Read' },
];

const RecipientsModal = ({ shiftId, open, onOpenChange, shiftLabel }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');

  const fetchIt = async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/shifts/${shiftId}/recipients`);
      setData(res.data);
    } catch (e) {
      setData({ total_recipients: 0, channel_summary: {}, recipients: [],
               error: e.response?.data?.detail || 'Failed to load recipients' });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchIt(); /* eslint-disable-next-line */ }, [open, shiftId]);

  const base = (data?.recipients || []).filter((r) => {
    if (tab === 'sent' && !r.sent) return false;
    if (tab === 'read' && !r.read) return false;
    return true;
  });
  const filtered = base.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.city || '').toLowerCase().includes(q) ||
      (r.state || '').toLowerCase().includes(q)
    );
  });

  const tabCounts = {
    all: (data?.recipients || []).length,
    sent: (data?.recipients || []).filter((r) => r.sent).length,
    read: (data?.recipients || []).filter((r) => r.read).length,
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${BACKEND}/api/admin/shifts/${shiftId}/recipients.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('CSV download failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recipients_${shiftId.slice(0, 8)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // silent — button will flash if fails
    }
  };

  const channelPills = Object.entries(data?.channel_summary || {}).map(([ch, by]) => {
    const Icon = CHANNEL_ICON[ch] || Bell;
    const sent = by.sent || 0;
    const errored = Object.entries(by).filter(([k]) => k !== 'sent').reduce((a, [, v]) => a + (v || 0), 0);
    return (
      <Badge key={ch} variant="outline" className="gap-1">
        <Icon className="w-3 h-3" /> {ch}: {sent} sent{errored ? ` · ${errored} failed` : ''}
      </Badge>
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="recipients-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-cyan-600" />
            Who was this sent to?
          </DialogTitle>
          <DialogDescription className="sr-only">Who was this sent to?</DialogDescription>
          {shiftLabel && <p className="text-xs text-gray-500">{shiftLabel}</p>}
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 flex-wrap pb-2 border-b">
          <div className="flex items-center gap-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`recipients-tab-${t.key}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                  tab === t.key
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-white hover:bg-gray-100 border-gray-300 text-gray-700'
                }`}
              >
                {t.label} <span className="opacity-70">({tabCounts[t.key] ?? 0})</span>
              </button>
            ))}
            <span className="mx-1 h-4 w-px bg-gray-200" />
            {channelPills}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search name/email/city…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 text-xs border rounded px-2 w-48"
              data-testid="recipients-search"
            />
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={loading || !data?.recipients?.length} data-testid="recipients-csv-btn">
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={fetchIt} disabled={loading} data-testid="recipients-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
            </div>
          ) : data?.error ? (
            <p className="text-sm text-rose-600 text-center py-6">{data.error}</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10">
              {query
                ? 'No recipients match that search.'
                : 'No notifications have been sent for this job yet. Use "Invite Talent" to send one.'}
            </p>
          ) : (
            <table className="w-full text-xs" data-testid="recipients-table">
              <thead className="text-left text-gray-500 border-b">
                <tr>
                  <th className="py-2 pr-3">Talent</th>
                  <th className="py-2 pr-3">Email / Phone</th>
                  <th className="py-2 pr-3">Location</th>
                  <th className="py-2 pr-3">Distance</th>
                  <th className="py-2 pr-3">Channels</th>
                  <th className="py-2 pr-3">Sent</th>
                  <th className="py-2 pr-3">Read</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.talent_id} className="border-b last:border-b-0 hover:bg-gray-50"
                      data-testid={`recipient-row-${r.talent_id}`}>
                    <td className="py-1.5 pr-3">
                      <div className="flex items-center gap-2">
                        {r.profile_photo_url ? (
                          <img src={r.profile_photo_url} className="w-7 h-7 rounded-full object-cover" alt="" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-semibold">
                            {(r.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-800">{r.name}</div>
                          {r.tier && <div className="text-[10px] text-gray-400 capitalize">{r.tier}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-1.5 pr-3">
                      <div className="text-gray-700">{r.email}</div>
                      <div className="text-[10px] text-gray-400">{r.phone}</div>
                    </td>
                    <td className="py-1.5 pr-3 text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {[r.city, r.state].filter(Boolean).join(', ') || '—'}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-gray-600 whitespace-nowrap">
                      {r.distance_miles != null ? (
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          r.distance_miles <= 70 ? 'bg-emerald-100 text-emerald-800'
                            : r.distance_miles <= 150 ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-800'
                        }`}>
                          {r.distance_miles} mi
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3">
                      <div className="flex gap-1 flex-wrap">
                        {(r.channels || []).map((ch) => {
                          const Icon = CHANNEL_ICON[ch] || Bell;
                          return (
                            <span key={ch} className="inline-flex items-center gap-0.5 bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 text-[10px]">
                              <Icon className="w-2.5 h-2.5" /> {ch}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-1.5 pr-3 text-gray-500 whitespace-nowrap">
                      {r.sent && <Check className="inline w-3.5 h-3.5 text-emerald-600" />}{' '}
                      {r.first_sent_at ? new Date(r.first_sent_at).toLocaleString() : '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-gray-500">
                      {r.read ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCheck className="w-3.5 h-3.5" /> Read
                        </span>
                      ) : (
                        <span className="text-gray-400">Not yet</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecipientsModal;
